import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";

export default function MarqueeCartHeader({
  text = "WWW.FAMBI.COM.MX",
  repeat = 4,
  speed = 32,
  minItemWidth = "1400px",
}) {
  const ref = useRef(null);
  const [scale, setScale] = useState(0.3); // default scale
  const [inView, setInView] = useState(false);

  // Intersection observer to check if component is in viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting);
      },
      { threshold: 0.1 } // visible if at least 10% in viewport
    );

    if (ref.current) observer.observe(ref.current);
    return () => {
      if (ref.current) observer.unobserve(ref.current);
    };
  }, []);

  // Scroll listener only when in view
  useEffect(() => {
    if (!inView) {
      setScale(1); // reset to normal when out of view
      return;
    }

    const handleScroll = () => {
      const scrollY = window.scrollY;
      // Example formula: between 1 and 1.8 while visible
      const newScale = 0.5 + Math.min(scrollY / 600, 1);
      setScale(newScale);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [inView]);

  const items = Array.from({ length: repeat }, (_, i) => (
    <Box
      key={i}
      component="span"
      sx={{
        display: "inline-block",
        minWidth: minItemWidth,
        whiteSpace: "nowrap",
        pr: 2,
      }}
    >
      {text}
    </Box>
  ));

  return (
    <Box
      ref={ref}
      component="section"
      sx={{
        overflow: "hidden",
        py: { xs: 3, md: 5 },
        bgcolor: "transparent",
      }}
      aria-label="Marquee heading"
    >
        <Box
        id="horizontalSlider"
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "center",
          transformOrigin: "0 50%",
          transform: `translate(-50%, 0) scale(${scale})`,
          position: "relative",
          transition: "transform 0.5s ease-out",
        }}
      >
        <Box
          component="div"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            animation: `marquee ${speed}s linear infinite`,
          }}
        >
          <Typography
            component="div"
            variant="h1"
            sx={{
              fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
              fontWeight: 700,
              textTransform: "uppercase",
              lineHeight: 1.4,
              fontSize: { xs: "2.5rem", md: "5.5rem", lg: "6.5rem" },
              color: (theme) => theme.palette.secondary.main,
              display: "flex",
              alignItems: "center",
            }}
          >
            {items}
            {items}
          </Typography>
        </Box>
      </Box>

      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        #horizontalSlider > div > div {
          min-width: 200%;
        }
      `}</style>
    </Box>
  );
}
